// src/pages/WalletStatement.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../utils/axiosInstance";

const DEFAULT_LIMIT = 100;

export default function WalletStatement() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const [wallet, setWallet] = useState(null);
  const [pairs, setPairs] = useState(null);

  // transactions with pagination
  const [txns, setTxns] = useState([]);
  const [txnPage, setTxnPage] = useState(1);
  const [txnTotal, setTxnTotal] = useState(0);
  const [txnHasMore, setTxnHasMore] = useState(false);
  const [txnLoadingMore, setTxnLoadingMore] = useState(false);

  // filters
  const [typeFilter, setTypeFilter] = useState("ALL");
  const [timeFilter, setTimeFilter] = useState("ALL"); // ALL, TODAY, CUSTOM
  const [statusFilter, setStatusFilter] = useState("ALL"); // ALL, LOCKED, UNLOCKED
  const [selectedDate, setSelectedDate] = useState("");
  const [q, setQ] = useState("");

  const token = localStorage.getItem("token") || localStorage.getItem("authToken") || "";

  const money = (v) => {
    const n = Number(v || 0);
    if (Number.isNaN(n)) return "₹ 0.00";
    return `₹ ${n.toFixed(2)}`;
  };

  const safeUpper = (v) => String(v || "").toUpperCase();

  const fmtDate = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleString(undefined, {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  };

  const getTxnDirection = (t) => {
    const type = safeUpper(t?.type || t?.txnType || "");
    if (type.includes("CREDIT")) return "CREDIT";
    if (type.includes("DEBIT")) return "DEBIT";
    const amt = Number(t?.amount ?? 0);
    return amt >= 0 ? "CREDIT" : "DEBIT";
  };

  const pairsByTxnId = useMemo(() => {
    const map = new Map();
    const matches = pairs?.matches;
    if (!Array.isArray(matches)) return map;
    for (const m of matches) {
      const key = String(m?.walletTransactionId ?? "");
      if (!key) continue;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(m);
    }
    return map;
  }, [pairs]);

  const buildTxnText = (t) => {
    const reason = safeUpper(t?.reason || "");
    const dt = fmtDate(t?.createdAt);

    if (reason === "PAIR_BONUS") {
      const arr = pairsByTxnId.get(String(t?.id)) || [];
      if (arr.length > 0) {
        const first = arr[0];
        const leftName = first?.leftUser?.name || first?.leftUser?.userID || (first?.leftUserId != null ? `#${first.leftUserId}` : "—");
        const rightName = first?.rightUser?.name || first?.rightUser?.userID || (first?.rightUserId != null ? `#${first.rightUserId}` : "—");
        return {
          title: `Pair Bonus${dt ? ` (${dt})` : ""}`,
          sub1: `Pairs Count: ${arr.length}`,
          sub2: null,
        };
      }
      return {
        title: `Pair Bonus${dt ? ` (${dt})` : ""}`,
        sub1: "Pair matched",
        sub2: "Paired members not available",
      };
    }

    if (reason === "REFERRAL_JOIN_BONUS" && t?.meta) {
      const refName = t.meta.referredName || t.meta.referredUserId || "Unknown";
      const pos = t.meta.placedPosition || "—";
      return {
        title: `Referral Join Bonus${dt ? ` (${dt})` : ""}`,
        sub1: `Joined User: ${refName}`,
        sub2: `Position: ${pos}`,
      };
    }

    if (reason === "WITHDRAWAL_REQUEST") {
      const st = safeUpper(t?.status || "");
      const statusLabel = st === "APPROVED" ? "Completed"
        : st === "REJECTED" ? "Rejected"
          : st === "PENDING" ? "Pending"
            : st || "—";
      return {
        title: `Withdrawal Request${dt ? ` (${dt})` : ""}`,
        sub1: `Status: ${statusLabel}`,
        sub2: t?.adminNote ? `Admin Note: ${t.adminNote}` : (t?.transactionId ? `Txn ID: ${t.transactionId}` : null),
        withdrawalStatus: st,
      };
    }

    const title = reason ? reason.replaceAll("_", " ") : "Transaction";
    return {
      title: `${title}${dt ? ` (${dt})` : ""}`,
      sub1: `Reason: ${t?.reason ?? "—"}`,
      sub2: null,
    };
  };

  // ── helpers to parse txn list from any response shape ────────────────────
  const parseTxnResponse = (data) => {
    if (Array.isArray(data)) return { list: data, total: data.length };
    const list = data?.transactions || data?.data || data?.rows || [];
    const serverTotal = data?.total ?? data?.totalCount ?? data?.count;
    // If no total in API but list has 10 items, assume there are more (999999 is a proxy for "more")
    const total = serverTotal !== undefined ? Number(serverTotal) : (list.length >= DEFAULT_LIMIT ? 999999 : list.length);
    return { list: Array.isArray(list) ? list : [], total };
  };

  // ── initial full load ─────────────────────────────────────────────────────
  const loadData = async () => {
    if (!token) { navigate("/login", { replace: true }); return; }
    const headers = { Authorization: `Bearer ${token}` };

    try {
      setErr("");
      setLoading(true);

      let params = { limit: DEFAULT_LIMIT, page: 1 };
      if (timeFilter === "TODAY") {
        params.today = true;
      } else if (timeFilter === "CUSTOM" && selectedDate) {
        params.date = selectedDate;
      }
      if (statusFilter === "LOCKED") {
        params.status = "locked";
      } else if (statusFilter === "UNLOCKED") {
        params.status = "unlocked";
      }

      const [wRes, tRes, pRes] = await Promise.all([
        axiosInstance.get("/api/wallet", { headers }),
        axiosInstance.get("/api/wallet/transactions", { headers, params }),
        axiosInstance.get("/api/pairs/my", { headers }).catch(() => ({ data: null })),
      ]);

      setWallet(wRes?.data || null);
      setPairs(pRes?.data || null);

      const { list, total } = parseTxnResponse(tRes?.data);
      const sorted = [...list].sort((a, b) => new Date(b?.createdAt || 0) - new Date(a?.createdAt || 0));
      setTxns(sorted);
      setTxnTotal(total);
      setTxnPage(1);
      
      // ✅ USER FIX: If we got 10 items, assume there are more (even if total is missing/wrong)
      setTxnHasMore(list.length >= DEFAULT_LIMIT);
    } catch (e) {
      setErr(e?.response?.data?.msg || e?.response?.data?.message || e?.message || "Failed to load wallet statement");
      setWallet(null); setTxns([]); setPairs(null);
    } finally {
      setLoading(false);
    }
  };

  // ── load more transactions (Pagination) ─────────────────
  const loadMoreTxns = async () => {
    if (txnLoadingMore) return;
    try {
      setTxnLoadingMore(true);
      const headers = { Authorization: `Bearer ${token}` };
      const nextPage = txnPage + 1;

      let params = { limit: DEFAULT_LIMIT, page: nextPage };
      if (timeFilter === "TODAY") {
        params.today = true;
      } else if (timeFilter === "CUSTOM" && selectedDate) {
        params.date = selectedDate;
      }
      if (statusFilter === "LOCKED") {
        params.status = "locked";
      } else if (statusFilter === "UNLOCKED") {
        params.status = "unlocked";
      }

      const res = await axiosInstance.get("/api/wallet/transactions", {
        headers,
        params,
      });

      const { list, total } = parseTxnResponse(res?.data);
      if (list.length === 0) {
        setTxnHasMore(false);
      } else {
        const nextTxns = [...txns, ...list];
        // ✅ USER FIX: De-duplicate based on ID
        const map = new Map();
        for (const t of nextTxns) {
          if (t?.id) map.set(String(t.id), t);
        }
        const unique = Array.from(map.values());

        // Sort to ensure chronological order doesn't jump
        const sorted = unique.sort((a, b) => new Date(b?.createdAt || 0) - new Date(a?.createdAt || 0));
        setTxns(sorted);
        setTxnPage(nextPage);
        setTxnTotal(total > sorted.length ? total : sorted.length);
        setTxnHasMore(list.length >= DEFAULT_LIMIT);
      }
    } catch (e) {
      setErr(e?.response?.data?.msg || e?.message || "Failed to load more");
    } finally {
      setTxnLoadingMore(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeFilter, selectedDate, statusFilter]);

  // ── totals from loaded txns ───────────────────────────────────────────────
  const totals = useMemo(() => {
    let credit = 0, debit = 0;
    for (const t of txns) {
      const dir = getTxnDirection(t);
      const abs = Math.abs(Number(t?.amount ?? 0) || 0);
      if (dir === "CREDIT") credit += abs;
      else debit += abs;
    }
    return { credit, debit };
  }, [txns]);

  // ── wallet values ─────────────────────────────────────────────────────────
  const walletBalance = wallet?.balance ?? wallet?.availableBalance ?? wallet?.walletBalance ?? 0;
  const lockedAmount = wallet?.lockedBalance ?? wallet?.lockedAmount ?? wallet?.locked ?? wallet?.holdAmount ?? wallet?.freezeAmount ?? 0;

  // ✅ USER REQUEST: Total Balance should show total earned (withdrawals don't reduce it)
  // We use the sum of all credits from the transactions if available.
  const totalEarningsFromBackend = wallet?.totalEarnings ?? wallet?.totalCredit ?? wallet?.totalCredits ?? 0;
  const totalEarnings = totalEarningsFromBackend > 0 ? totalEarningsFromBackend : totals.credit;

  const AmountBadge = ({ direction, amount, txn }) => {
    const isCredit = direction === "CREDIT";
    const reason = safeUpper(txn?.reason || "");
    const isBonus = reason.includes("BONUS") || reason.includes("COMMISSION") || reason.includes("INCOME");
    
    // ✅ Use meta.pending for accurate lock status
    const isLocked = isBonus && (txn?.meta?.pending === true || (txn?.meta?.pending === undefined && txn?.isUnlocked !== true));
    const isUnlocked = isBonus && (txn?.meta?.pending === false || txn?.isUnlocked === true);

    const pendingReason = txn?.meta?.pendingReason;

    return (
      <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "6px" }}>
        <span className={"amt " + (isCredit ? "credit" : "debit")}>
          <span className="arrowPill">
            {isLocked ? "🔒" : isCredit ? "↗" : "↘"}
          </span>
          <span style={{ fontWeight: 950 }}>{isCredit ? "+" : "-"} {money(Math.abs(amount))}</span>
        </span>
        {isLocked && (
          <span style={{
            fontSize: "11px",
            fontWeight: 800,
            color: "#b91c1c",
            background: "rgba(239,68,68,.1)",
            padding: "4px 8px",
            borderRadius: "999px",
            border: "1px solid rgba(239,68,68,.2)"
          }}>
            🔒 Locked Balance
          </span>
        )}
        {isUnlocked && (
          <span style={{
            fontSize: "11px",
            fontWeight: 800,
            color: "#166534",
            background: "rgba(34,197,94,.12)",
            padding: "4px 8px",
            borderRadius: "999px",
            border: "1px solid rgba(34,197,94,.30)"
          }}>
            🔓 Released to Wallet
          </span>
        )}
      </div>
    );
  };

  // ── filtered list ─────────────────────────────────────────────────────────
  const filtered = useMemo(() => {
    const query = String(q || "").trim().toLowerCase();
    return txns.filter((t) => {
      const dir = getTxnDirection(t);
      if (typeFilter !== "ALL" && dir !== typeFilter) return false;
      if (!query) return true;
      const reason = String(t?.reason || "").toLowerCase();
      const id = String(t?.id ?? "").toLowerCase();
      const created = String(t?.createdAt ?? "").toLowerCase();
      let pairText = "";
      if (safeUpper(t?.reason) === "PAIR_BONUS") {
        const arr = pairsByTxnId.get(String(t?.id)) || [];
        if (arr.length > 0) {
          const first = arr[0];
          pairText = `${first?.leftUser?.name || ""} ${first?.rightUser?.name || ""}`.toLowerCase();
        }
      }
      return reason.includes(query) || id.includes(query) || created.includes(query) || pairText.includes(query);
    });
  }, [txns, typeFilter, q, pairsByTxnId]);

  return (
    <div style={S.page}>
      <style>{css}</style>

      <div style={S.container}>

        {loading && (
          <div style={S.info}>
            <span style={S.spinner} /> Loading wallet…
          </div>
        )}
        {err && <div style={{ ...S.info, ...S.infoErr }}>{err}</div>}

        {!loading && !err && (
          <>
            {/* ── SUMMARY CARDS ─────────────────────────────────────────── */}
            <div className="gridTop">

              {/* Current Balance — GREEN */}
              <div style={{ ...S.card, ...S.cardGreen }}>
                <div style={S.cardTopRow}>
                  <div style={{ ...S.cardIcon, background: "rgba(34,197,94,.15)", color: "#16a34a" }}>💰</div>
                  <span style={{ ...S.cardBadge, background: "rgba(34,197,94,.12)", color: "#15803d", borderColor: "rgba(34,197,94,.30)" }}>AVAILABLE</span>
                </div>
                <div style={S.cardTitle}>Current Balance</div>
                <div style={{ ...S.balance, color: "#15803d" }}>{money(walletBalance)}</div>
                <div style={S.cardSub}>Wallet ID: {wallet?.id ?? "—"}</div>
              </div>

              {/* Locked Amount — RED */}
              <div style={{ ...S.card, ...S.cardRed }}>
                <div style={S.cardTopRow}>
                  <div style={{ ...S.cardIcon, background: "rgba(239,68,68,.12)", color: "#dc2626" }}>🔒</div>
                  <span style={{ ...S.cardBadge, background: "rgba(239,68,68,.10)", color: "#b91c1c", borderColor: "rgba(239,68,68,.28)" }}>LOCKED</span>
                </div>
                <div style={S.cardTitle}>Locked Amount</div>
                <div style={{ ...S.balance, color: "#b91c1c" }}>{money(lockedAmount)}</div>
                <div style={S.cardSub}>Held / Frozen funds</div>
              </div>

              {/* Total Balance — BLUE */}
              <div style={{ ...S.card, ...S.cardBlue }}>
                <div style={S.cardTopRow}>
                  <div style={{ ...S.cardIcon, background: "rgba(37,99,235,.15)", color: "#1d4ed8" }}>💳</div>
                  <span style={{ ...S.cardBadge, background: "rgba(37,99,235,.12)", color: "#1d4ed8", borderColor: "rgba(37,99,235,.30)" }}>TOTAL EARNED</span>
                </div>
                <div style={S.cardTitle}>Total Earnings</div>
                <div style={{ ...S.balance, color: "#1d4ed8" }}>{money(totalEarnings)}</div>
                <div style={S.cardSub}>Overall Income</div>
              </div>

            </div>

            {/* ── TRANSACTIONS ──────────────────────────────────────────── */}
            <div style={S.section}>
              <div className="rowTop">
                <div style={S.sectionTitle}>
                  Transactions
                  {txnTotal > 0 && (
                    <span style={S.txnCount}>{txns.length} / {txnTotal}</span>
                  )}
                </div>

                <div className="filters">
                  <div className="seg">
                    {["ALL", "CREDIT", "DEBIT"].map((f) => (
                      <button key={f} className={"segBtn " + (typeFilter === f ? "on" : "")} onClick={() => {
                        setTypeFilter(f);
                        if (f !== "ALL") setStatusFilter("ALL");
                      }}>
                        {f === "ALL" ? "All" : f === "CREDIT" ? "Credit" : "Debit"}
                      </button>
                    ))}
                  </div>

                  <div className="seg">
                    {["ALL", "LOCKED", "UNLOCKED"].map((f) => (
                      <button key={f} className={"segBtn " + (statusFilter === f ? "on" : "")} onClick={() => {
                        setStatusFilter(f);
                        if (f !== "ALL") setTypeFilter("ALL");
                      }}>
                        {f === "ALL" ? "All Status" : f === "LOCKED" ? "Locked" : "Unlocked"}
                      </button>
                    ))}
                  </div>

                  <div className="seg">
                    <button 
                      className={"segBtn " + (timeFilter === "ALL" ? "on" : "")} 
                      onClick={() => setTimeFilter("ALL")}
                    >
                      All Time
                    </button>
                    <button 
                      className={"segBtn " + (timeFilter === "TODAY" ? "on" : "")} 
                      onClick={() => setTimeFilter("TODAY")}
                    >
                      Today
                    </button>
                    <button 
                      className={"segBtn " + (timeFilter === "CUSTOM" ? "on" : "")} 
                      onClick={() => setTimeFilter("CUSTOM")}
                    >
                      Date
                    </button>
                  </div>

                  {timeFilter === "CUSTOM" && (
                    <input
                      type="date"
                      className="search"
                      style={{ width: "130px" }}
                      value={selectedDate}
                      onChange={(e) => setSelectedDate(e.target.value)}
                    />
                  )}
                  <input
                    className="search"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Search reason / id / date / pair name…"
                  />
                  <button style={S.refreshBtn} onClick={loadData} disabled={loading}>↺ Refresh</button>
                </div>
              </div>

              {filtered.length === 0 ? (
                <div style={S.info}>No transactions found.</div>
              ) : (
                <>
                  <div className="txHead">
                    <div>Details</div>
                    <div style={{ textAlign: "right" }}>Amount</div>
                  </div>

                  {filtered.map((t, idx) => {
                    const amt = Number(t?.amount ?? 0);
                    const dir = getTxnDirection(t);
                    const { title, sub1, sub2, withdrawalStatus } = buildTxnText(t);

                    // Status badge for withdrawals
                    const wdStatusBadge = withdrawalStatus ? (() => {
                      const isApp = withdrawalStatus === "APPROVED";
                      const isPen = withdrawalStatus === "PENDING";
                      const badgeStyle = isApp
                        ? { background: "rgba(34,197,94,.12)", border: "1px solid rgba(34,197,94,.30)", color: "#166534" }
                        : isPen
                          ? { background: "rgba(245,158,11,.12)", border: "1px solid rgba(245,158,11,.30)", color: "#92400e" }
                          : { background: "rgba(239,68,68,.10)", border: "1px solid rgba(239,68,68,.28)", color: "#991b1b" };
                      const label = isApp ? "✅ Completed" : isPen ? "⏳ Pending" : "❌ Rejected";
                      return <span style={{ fontSize: 11, fontWeight: 800, padding: "2px 9px", borderRadius: 999, ...badgeStyle }}>{label}</span>;
                    })() : null;

                    return (
                      <div className="txRow" key={t?.id ?? idx}>
                        <div>
                          <div style={{ fontWeight: 950, display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                            <span>{title}</span>
                            <span className={"tag " + (dir === "CREDIT" ? "green" : "red")}>{dir}</span>
                            {safeUpper(t?.reason) === "PAIR_BONUS" && <span className="pairTag">PAIR</span>}
                            {wdStatusBadge}
                          </div>
                          <div style={{ marginTop: 6, fontSize: 12, color: "rgba(15,23,42,.68)", lineHeight: 1.5 }}>
                            {sub1 && <div>{sub1}</div>}
                            {sub2 && <div style={{ marginTop: 3, color: "rgba(15,23,42,.52)" }}>{sub2}</div>}
                            {t?.meta?.releasedAt && (
                              <div style={{ marginTop: 4, color: "#166534", fontWeight: 700, display: "flex", alignItems: "center", fontSize: "11px" }}>
                                <span style={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  width: "14px",
                                  height: "14px",
                                  borderRadius: "50%",
                                  background: "#16a34a",
                                  color: "#fff",
                                  fontSize: "9px",
                                  marginRight: "6px",
                                  flexShrink: 0
                                }}>✓</span>
                                Released on {fmtDate(t.meta.releasedAt)}
                              </div>
                            )}
                          </div>
                        </div>
                        <div style={{ textAlign: "right" }}>
                          <AmountBadge direction={dir} amount={amt} txn={t} />
                        </div>
                      </div>
                    );
                  })}
                </>
              )}

              {/* ── LOAD MORE ─────────────────────────────────────────── */}
              {/* ✅ Load More Button: Fetches next page of 10 items */}
              {txnHasMore && !q && typeFilter === "ALL" && (
                <div style={S.loadMoreWrap}>
                  <button
                    style={txnLoadingMore ? { ...S.loadMoreBtn, opacity: 0.7, cursor: "not-allowed" } : S.loadMoreBtn}
                    onClick={loadMoreTxns}
                    disabled={txnLoadingMore}
                  >
                    {txnLoadingMore
                      ? <><span style={S.spinner} /> Loading...</>
                      : <>Load More Transactions ({txns.length} / {txnTotal === 999999 ? "..." : txnTotal})</>
                    }
                  </button>
                </div>
              )}

              {!txnHasMore && txns.length > 0 && !q && typeFilter === "ALL" && (
                <div style={S.endMsg}>✓ All {txns.length} transaction{txns.length !== 1 ? "s" : ""} loaded</div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/* ─── CSS ─────────────────────────────────────────────────────────────────── */
const css = `
  *{ margin:0; padding:0; box-sizing:border-box; }
  body{ background:#ffffff; color:#0f172a; font-family: ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto; }
  button{ font-family: inherit; }

  @keyframes spin { to { transform: rotate(360deg); } }

  .gridTop{
    display:grid;
    grid-template-columns: repeat(3, minmax(0,1fr));
    gap:12px;
    margin-bottom:16px;
  }
  @media (max-width:1100px){ .gridTop{ grid-template-columns: 1fr 1fr; } }
  @media (max-width:560px){ .gridTop{ grid-template-columns: 1fr; } }

  .rowTop{
    display:flex;
    justify-content:space-between;
    align-items:flex-end;
    gap:12px;
    flex-wrap:wrap;
    margin-bottom:10px;
  }

  .filters{
    display:flex;
    gap:8px;
    flex-wrap:wrap;
    align-items:center;
  }

  .seg{
    display:flex;
    border:1px solid rgba(15,23,42,.12);
    border-radius:14px;
    overflow:hidden;
    background:rgba(15,23,42,.03);
  }
  .segBtn{
    height:38px;
    padding:0 12px;
    border:none;
    background:transparent;
    font-weight:800;
    cursor:pointer;
    color:rgba(15,23,42,.65);
    font-size:13px;
    transition:background .13s,color .13s;
  }
  .segBtn.on{ background:rgba(37,99,235,.10); color:#1d4ed8; }

  .search{
    height:38px;
    width:min(280px,100%);
    border-radius:12px;
    border:1px solid rgba(15,23,42,.12);
    padding:0 12px;
    outline:none;
    background:#fff;
    color:#0f172a;
    font-size:13px;
    font-weight:700;
  }
  .search:focus{ border-color:rgba(37,99,235,.35); box-shadow:0 0 0 3px rgba(37,99,235,.10); }

  .txHead{
    display:grid;
    grid-template-columns:1fr 200px;
    gap:10px;
    padding:10px 12px;
    border-radius:12px;
    border:1px solid rgba(15,23,42,.10);
    background:rgba(15,23,42,.03);
    font-weight:800;
    font-size:13px;
    color:rgba(15,23,42,.70);
    margin-bottom:6px;
  }
  .txRow{
    display:grid;
    grid-template-columns:1fr 200px;
    gap:10px;
    padding:12px 14px;
    border-radius:14px;
    border:1px solid rgba(15,23,42,.09);
    background:#fff;
    box-shadow:0 8px 18px rgba(0,0,0,.05);
    margin-top:8px;
    align-items:center;
    transition:box-shadow .15s;
  }
  .txRow:hover{ box-shadow:0 12px 28px rgba(0,0,0,.09); }

  @media (max-width:700px){
    .txHead{ display:none; }
    .txRow{ grid-template-columns:1fr; }
  }

  .tag{
    font-size:11px; font-weight:800;
    border-radius:999px; padding:4px 9px;
    border:1px solid rgba(15,23,42,.12);
    background:rgba(15,23,42,.03);
  }
  .tag.green{ border-color:rgba(34,197,94,.25); background:rgba(34,197,94,.10); color:#065f46; }
  .tag.red{ border-color:rgba(239,68,68,.25); background:rgba(239,68,68,.08); color:#7f1d1d; }

  .pairTag{
    font-size:11px; font-weight:800;
    border-radius:999px; padding:4px 9px;
    border:1px solid rgba(37,99,235,.25);
    background:rgba(37,99,235,.10);
    color:#1d4ed8;
  }

  .amt{
    display:inline-flex; align-items:center; gap:8px;
    padding:7px 11px;
    border-radius:999px;
    border:1px solid rgba(15,23,42,.12);
    background:rgba(15,23,42,.03);
    font-weight:950; font-size:13px; white-space:nowrap; color:#0f172a;
  }
  .amt.credit{ border-color:rgba(34,197,94,.25); background:rgba(34,197,94,.10); color:#065f46; }
  .amt.debit{ border-color:rgba(239,68,68,.25); background:rgba(239,68,68,.08); color:#7f1d1d; }

  .arrowPill{
    width:24px; height:24px; border-radius:999px;
    display:grid; place-items:center;
    border:1px solid rgba(15,23,42,.12);
    background:rgba(255,255,255,.90);
    font-weight:950; font-size:13px; line-height:1;
  }
`;

/* ─── STYLES ──────────────────────────────────────────────────────────────── */
const S = {
  page: { minHeight: "100vh", background: "#ffffff", padding: "18px 0 60px" },
  container: { width: "min(1200px,100%)", margin: "0 auto", padding: "0 16px" },

  info: {
    padding: "12px 14px", borderRadius: 12,
    background: "#fff", border: "1px solid rgba(15,23,42,.10)",
    boxShadow: "0 8px 18px rgba(0,0,0,.05)",
    marginBottom: 12, color: "rgba(15,23,42,.75)", fontWeight: 700,
    display: "flex", alignItems: "center", gap: 8,
  },
  infoErr: {
    borderColor: "rgba(239,68,68,.25)", background: "rgba(239,68,68,.06)", color: "#7f1d1d",
  },

  spinner: {
    display: "inline-block", width: 14, height: 14,
    border: "2px solid rgba(15,23,42,.12)", borderTopColor: "#2563eb",
    borderRadius: "50%", animation: "spin .7s linear infinite", flexShrink: 0,
  },

  /* cards */
  card: {
    borderRadius: 18, border: "1px solid rgba(15,23,42,.10)",
    background: "#ffffff", padding: "16px 14px",
    boxShadow: "0 10px 22px rgba(0,0,0,.06)", minWidth: 0,
  },
  cardGreen: {
    borderColor: "rgba(34,197,94,.22)",
    background: "linear-gradient(140deg,#f0fdf4 0%,#fff 60%)",
    boxShadow: "0 10px 28px rgba(34,197,94,.10)",
  },
  cardRed: {
    borderColor: "rgba(239,68,68,.22)",
    background: "linear-gradient(140deg,#fff1f2 0%,#fff 60%)",
    boxShadow: "0 10px 28px rgba(239,68,68,.08)",
  },
  cardBlue: {
    borderColor: "rgba(37,99,235,.22)",
    background: "linear-gradient(140deg,#eff6ff 0%,#fff 60%)",
    boxShadow: "0 10px 28px rgba(37,99,235,.08)",
  },
  cardTopRow: { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 },
  cardIcon: {
    width: 36, height: 36, borderRadius: 10,
    display: "grid", placeItems: "center", fontSize: 18,
  },
  cardBadge: {
    fontSize: 10, fontWeight: 800, borderRadius: 999,
    padding: "3px 9px", border: "1px solid",
  },
  cardTitle: { fontSize: 12, color: "rgba(15,23,42,.58)", fontWeight: 700, marginBottom: 4 },
  balance: { fontSize: 26, fontWeight: 950, color: "#0f172a", letterSpacing: "-0.5px" },
  cardSub: { marginTop: 6, fontSize: 11, color: "rgba(15,23,42,.55)", fontWeight: 600 },

  section: { marginTop: 4 },
  sectionTitle: {
    fontSize: 18, fontWeight: 950, color: "#0f172a",
    display: "flex", alignItems: "center", gap: 10,
  },
  txnCount: {
    fontSize: 12, fontWeight: 700,
    padding: "3px 10px", borderRadius: 999,
    border: "1px solid rgba(14,165,233,.25)",
    background: "rgba(14,165,233,.08)", color: "#0369a1",
  },

  refreshBtn: {
    height: 38, padding: "0 14px", borderRadius: 12,
    border: "1px solid rgba(37,99,235,.22)",
    background: "rgba(37,99,235,.08)", color: "#1d4ed8",
    fontWeight: 700, fontSize: 13, cursor: "pointer",
  },

  loadMoreWrap: { display: "flex", justifyContent: "center", marginTop: 18 },
  loadMoreBtn: {
    display: "flex", alignItems: "center", gap: 8,
    padding: "11px 28px", borderRadius: 14,
    border: "1px solid rgba(37,99,235,.25)",
    background: "rgba(37,99,235,.08)", color: "#1d4ed8",
    fontWeight: 800, fontSize: 14, cursor: "pointer",
  },

  endMsg: {
    marginTop: 18, textAlign: "center",
    fontSize: 13, color: "rgba(15,23,42,.42)", fontWeight: 600,
  },
};